import React, { use, useCallback, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useQuery } from "@apollo/client/react";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { StackScreenProps } from "@react-navigation/stack";
import NoPayrollBreakdownImage from "../../assets/images/no-salaries-image.svg";
import EmptyScreen from "../../components/EmptyScreen";
import ScreenCard from "../ScreenCard";
import { FIND_MISSION_PAYROLL_BREAKDOWN } from "../../graphql/timesheet";
import PayrollBreakdownList from "../../components/PayrollBreakdownList";
import { RootStackParamList } from "../../navigations/RootNavigation";
import ScreenHeader from "./ScreenHeader";
import { GET_MISSION } from "../../graphql/core";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { isPendingAutoSubmitPayrollBreakdown } from "../../components/editors/payroll-breakdown/utils";

type PayrollBreakdownListScreenProps = StackScreenProps<RootStackParamList, "PayrollBreakdownList">;

const PayrollBreakdownListScreen: React.FC<PayrollBreakdownListScreenProps> = ({
    route,
    navigation,
}) => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);

    const {
        loading: payrollLoading,
        error: payrollError,
        data: payrollData,
        refetch,
    } = useQuery(FIND_MISSION_PAYROLL_BREAKDOWN, {
        fetchPolicy: "cache-and-network",
        skip: !route.params?.missionId,
        variables: {
            missionId: route.params?.missionId,
        },
    });
    const {
        data: missionData,
        loading: missionLoading,
        error: missionError,
    } = useQuery(GET_MISSION, {
        variables: { id: route.params?.missionId || "" },
        skip: !route.params?.missionId,
    });

    const allPayrollBreakdowns = useMemo(
        () =>
            payrollData?.findMissionPayrollBreakdown.elements.filter(
                (pb) => !isPendingAutoSubmitPayrollBreakdown(pb),
            ),
        [payrollData?.findMissionPayrollBreakdown.elements],
    );

    const subTitle = useMemo(() => {
        if (missionLoading) {
            return i18n.t("loading");
        }
        return missionData?.getMission?.name || "";
    }, [i18n, missionData?.getMission?.name, missionLoading]);

    const onBack = useCallback(() => {
        navigation.goBack();
    }, [navigation]);

    return (
        <>
            <ScreenHeader
                label={i18n.t("screens.payroll_breakdown_list.title")}
                subLabel={subTitle}
                onLeftAction={onBack}
                useSmallLabel
            />
            <ScreenCard
                refresh={refetch}
                isContentContainerFlex={allPayrollBreakdowns?.length === 0}
            >
                <LoadingBoundary
                    error={payrollError || missionError}
                    loading={payrollLoading && !payrollData}
                    clearError={() => {
                        void refetch();
                    }}
                >
                    {allPayrollBreakdowns?.length === 0 ? (
                        <EmptyScreen
                            image={NoPayrollBreakdownImage}
                            text={i18n.t("screens.payroll_breakdown_list.no_breakdowns")}
                        />
                    ) : profileType && allPayrollBreakdowns ? (
                        <PayrollBreakdownList
                            profileType={profileType}
                            payrollBreakdowns={allPayrollBreakdowns}
                            navigation={navigation}
                        />
                    ) : null}
                </LoadingBoundary>
            </ScreenCard>
        </>
    );
};

export default PayrollBreakdownListScreen;
